import { ExternalLink } from "lucide-mui";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { MasterCompanyDetail } from "../../../api/client.ts";
import FormattedDate from "../../../components/FormattedDate.tsx";
import { formatNumber } from "../../../utils/format.ts";
import { formatAddress } from "../utils.ts";
import ClassifyButton from "./ClassifyButton.tsx";
import DetailRow from "../../../components/DetailRow.tsx";
import EditableWebsiteRow from "./EditableWebsiteRow.tsx";
import SectionCard from "../../../components/SectionCard.tsx";

interface DetailsCardProps {
  company: MasterCompanyDetail;
  companyId: string;
}

export default function DetailsCard({ company, companyId }: DetailsCardProps) {
  const c = company;
  return (
    <SectionCard title="Details">
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailRow label="Org number" value={c.org_number} />
          <DetailRow label="Industry" value={c.industry} />
          <DetailRow
            label="Employee count"
            value={c.employee_count != null ? formatNumber(c.employee_count) : null}
          />
          <EditableWebsiteRow company={c} />
          <DetailRow
            label="LinkedIn"
            value={
              c.linkedin_url ? (
                <Box>
                  <Link
                    href={c.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    {c.linkedin_url} <ExternalLink fontSize="inherit" />
                  </Link>
                  {c.field_updated_at?.linkedin_url && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      Updated <FormattedDate value={c.field_updated_at.linkedin_url} />
                    </Typography>
                  )}
                </Box>
              ) : null
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailRow label="Phone" value={c.phone} />
          <DetailRow label="Email" value={c.email} />
          <DetailRow label="Address" value={formatAddress(c.address)} />
          {c.description && <DetailRow label="Description" value={c.description} />}
        </Grid>
      </Grid>
      {(c.ai_summary ||
        (c.ai_industries && c.ai_industries.length > 0) ||
        (c.ai_served_verticals && c.ai_served_verticals.length > 0)) && (
        <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
          {c.ai_summary && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                AI summary
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {c.ai_summary}
              </Typography>
            </>
          )}
          {c.ai_industries && c.ai_industries.length > 0 && (
            <Box sx={{ mt: c.ai_summary ? 1.5 : 0 }}>
              <Typography variant="subtitle2" gutterBottom>
                AI industries
              </Typography>
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                {c.ai_industries.map((industry) => (
                  <Chip key={industry} label={industry} />
                ))}
              </Stack>
            </Box>
          )}
          {c.ai_served_verticals && c.ai_served_verticals.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>
                Serves verticals
              </Typography>
              <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                {c.ai_served_verticals.map((v) => (
                  <Chip key={v} label={v} />
                ))}
              </Stack>
            </Box>
          )}
          {(c.field_updated_at?.ai_summary || c.field_updated_at?.ai_industries) && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mt: 1 }}
            >
              AI data updated{" "}
              <FormattedDate
                value={c.field_updated_at.ai_summary ?? c.field_updated_at.ai_industries}
              />
            </Typography>
          )}
        </Box>
      )}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Created <FormattedDate value={c.created_at} /> · Updated{" "}
          <FormattedDate value={c.updated_at} />
        </Typography>
        <ClassifyButton companyId={companyId} hasExisting={!!c.ai_summary} />
      </Box>
    </SectionCard>
  );
}
